import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { fetchUser } from "../../shared/services";
import { analytics, WrapperStyles, getUsername } from "../../shared/utils";
import type { FBUser } from "../../shared/types";
import DownloadOverlay from "../overlays/DownloadOverlay";
import FooterOverlay from "../overlays/FooterOverlay";
import LoadingOverlay from "../overlays/LoadingOverlay";
import DynamicMeta from "../shared/DynamicMeta";

const PublicProfileView = () => {
  const { id: userId } = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [user, setUser] = useState<FBUser>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch referrer id if passed
  const [searchParams] = useSearchParams();
  const referrerId = searchParams.get("referrerId");

  useEffect(() => {
    analytics.screen("Web/Public/Profile", {
      userId: userId,
      referrerId: referrerId,
    });
  }, [userId, referrerId]);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchUser(userId)
      .then((user) => setUser(user))
      .catch((error) => {
        console.error(error);
        setUser(undefined);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  const UserOverlay = () => {
    if (!user) return null;
    const name = getUsername(user);
    return (
      <Card
        sx={{
          display: "flex",
          backgroundColor: "rgba(0, 0, 0, 0.87)",
          alignItems: "center",
        }}
      >
        <CardContent
          sx={{
            flex: "1 0 auto",
            display: "flex",
            flexDirection: "row",
            backgroundColor: "rgba(0, 0, 0, 0.87)",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            {user.profilePicture && (
              <Avatar
                src={user.profilePicture}
                alt={name}
                sx={{ width: 24, height: 24 }}
              />
            )}
            <Typography
              sx={{
                textAlign: "center",
                color: "white",
                fontSize: !isMobile ? 18 : 16,
                fontWeight: "bold",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.2,
                maxWidth: "80%",
                flex: 1,
              }}
            >
              {name} shared their Hooper profile
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const getMetaTitle = () => {
    const name = (user && getUsername(user)) || "Someone";
    return `${name}'s Hooper Profile`;
  };

  const getMetaDescription = () => {
    if (!user) return "Create your stats and highlights, shot by shot";
    const name = (user && getUsername(user)) || "someone";
    return `Check out ${name}'s basketball stats and highlights on Hooper. Join the community and start tracking your game.`;
  };

  const getMetaImage = () => {
    if (user?.profilePicture) return user.profilePicture;
    return "/logos/logo-white.svg";
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: "100vh",
        flexDirection: "column",
        display: "flex",
        px: "0 !important",
      }}
    >
      <DynamicMeta
        title={getMetaTitle()}
        description={getMetaDescription()}
        image={getMetaImage()}
        type="profile"
        url={`/profile/${userId}${referrerId ? `?referrerId=${referrerId}` : ""}`}
      />
      <Box sx={WrapperStyles}>
        <Box style={{ marginTop: 100 }} />
        {loading ? (
          <LoadingOverlay />
        ) : (
          <DownloadOverlay
            parentViewName="Profile"
            backgroundId="background-2.jpg"
            deepLinkPath={`/profile/${userId}${referrerId ? `?referrerId=${referrerId}` : ""}`}
            children={<UserOverlay />}
          />
        )}
        <Box sx={{ my: 8 }} />
        <FooterOverlay />
      </Box>
    </Container>
  );
};

export default PublicProfileView;
